"use client";

import { useRef, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Play, Pause } from "lucide-react";
import { getTombstoneText, type ContentType, type DeletedByType } from "@/lib/tombstone";
import { useAudioPlayer } from "@/lib/audio/AudioPlayerContext";

interface PostPlayerProps {
  audioUrl: string | null | undefined;
  durationMs: number;
  waveformUrl?: string | null;
  className?: string;
  contentType?: ContentType; // Required when audioUrl is null for proper tombstone text
  isOwner?: boolean; // Required when audioUrl is null for proper tombstone text
  deletedByType?: DeletedByType; // Required when audioUrl is null for proper tombstone text
  onPlay?: () => void; // Optional callback when audio starts playing
}

export interface PostPlayerRef {
  pause: () => void;
  isPlaying: () => boolean;
}

/**
 * Audio player component for playing posts
 * Shows play/pause button, progress bar, and timestamp
 */
export const PostPlayer = forwardRef<PostPlayerRef, PostPlayerProps>(({
  audioUrl,
  durationMs,
  waveformUrl,
  className = "",
  contentType = 'post',
  isOwner = false,
  deletedByType = 'user',
  onPlay,
}, ref) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const durationRef = useRef<number>(durationMs / 1000);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationMs / 1000);
  const animationFrameRef = useRef<number | null>(null);
  const isSeekingRef = useRef(false);
  const seekRectRef = useRef<DOMRect | null>(null); // Cache rect to reduce getBoundingClientRect calls
  const playerIdRef = useRef<string>(`player-${Math.random().toString(36).substr(2, 9)}-${Date.now()}`);
  const audioPlayerContext = useAudioPlayer();
  const onPlayRef = useRef(onPlay);

  // Expose pause method and playing state via ref
  useImperativeHandle(ref, () => ({
    pause: () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        setIsPlaying(false);
        setCurrentTime(0);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      }
    },
    isPlaying: () => {
      const audio = audioRef.current;
      return audio ? !audio.paused : false;
    },
  }));

  // Reset when audioUrl changes
  useEffect(() => {
    setCurrentTime(0);
    const initialDuration = durationMs / 1000;
    durationRef.current = initialDuration;
    setDuration(initialDuration);
    setIsPlaying(false);
    setHasPlayed(false);
    // Cancel any running animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, [audioUrl, durationMs]);

  // Keep onPlay ref up to date
  useEffect(() => {
    onPlayRef.current = onPlay;
  }, [onPlay]);

  // Register/unregister audio element with context (if available)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioPlayerContext) return;

    // Register the audio element
    audioPlayerContext.registerPlayer(playerIdRef.current, audio);

    // Unregister on cleanup
    return () => {
      audioPlayerContext.unregisterPlayer(playerIdRef.current);
    };
  }, [audioPlayerContext]);

  // Smooth progress updates using requestAnimationFrame
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (audio && !audio.paused) {
        setCurrentTime(audio.currentTime);
        // Also update duration from audio element in case it wasn't loaded initially
        const audioDuration = audio.duration;
        if (audioDuration && !isNaN(audioDuration) && isFinite(audioDuration) && audioDuration > 0) {
          durationRef.current = audioDuration;
          setDuration(audioDuration);
        }
        animationFrameRef.current = requestAnimationFrame(updateProgress);
      }
    };

    const handleDurationChange = () => {
      const audioDuration = audio.duration;
      if (audioDuration && !isNaN(audioDuration) && isFinite(audioDuration) && audioDuration > 0) {
        durationRef.current = audioDuration;
        setDuration(audioDuration);
      }
    };

    // Try to get duration immediately
    handleDurationChange();

    const handleEnded = () => {
      setIsPlaying(false);
      // Keep the UI at the end (full progress) instead of snapping back to 0
      setCurrentTime(durationRef.current);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      // Notify context that this player has ended
      if (audioPlayerContext) {
        audioPlayerContext.clearCurrentPlayer(playerIdRef.current);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setHasPlayed(true);
      onPlayRef.current?.(); // Call callback when audio starts (use ref to get latest)
      // Start the animation loop
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    };

    const handlePause = () => {
      setIsPlaying(false);
      // Stop the animation loop
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      // Notify context that this player has paused (if it was the current one)
      // Only clear if this pause wasn't triggered by user clicking pause (i.e., was paused by context)
      if (audioPlayerContext) {
        const audio = audioRef.current;
        // If audio is paused but we didn't just call pause in togglePlay, it was paused by context
        // We can detect this by checking if isPlaying was true before this pause
        // Actually, simpler: just clear if this was the current player
        audioPlayerContext.clearCurrentPlayer(playerIdRef.current);
      }
    };

    audio.addEventListener("loadedmetadata", handleDurationChange);
    audio.addEventListener("loadeddata", handleDurationChange);
    audio.addEventListener("canplay", handleDurationChange);
    audio.addEventListener("durationchange", handleDurationChange);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    // Force load metadata
    audio.load();

    // Try to get duration immediately
    handleDurationChange();

    return () => {
      audio.removeEventListener("loadedmetadata", handleDurationChange);
      audio.removeEventListener("loadeddata", handleDurationChange);
      audio.removeEventListener("canplay", handleDurationChange);
      audio.removeEventListener("durationchange", handleDurationChange);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      // Clean up animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      // Use context's play function if available (which will pause other playing audio)
      // Otherwise, play directly (for backwards compatibility)
      if (audioPlayerContext) {
        audioPlayerContext.play(playerIdRef.current);
      } else {
        audio.play().catch((error) => {
          console.error("Failed to play audio:", error);
        });
      }
    }
    // `isPlaying` is driven by the audio element's play/pause events.
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Use cached rect if available (from pointer handlers), otherwise calculate once
    // This reduces forced reflows during click events
    const rect = seekRectRef.current || e.currentTarget.getBoundingClientRect();
    // Cache the rect for potential subsequent operations
    if (!seekRectRef.current) {
      seekRectRef.current = rect;
    }
    
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
    
    // Clear cached rect after a short delay to allow for potential rapid clicks
    setTimeout(() => {
      if (!isSeekingRef.current) {
        seekRectRef.current = null;
      }
    }, 100);
  };

  const seekByClientX = (clientX: number, rect: DOMRect) => {
    const audio = audioRef.current;
    if (!audio) return;

    const pct = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    const clampedPct = Math.min(1, Math.max(0, pct));
    const seekDuration = durationRef.current > 0 ? durationRef.current : duration;
    const nextTime = clampedPct * (seekDuration || 0);
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Enable drag-to-seek on both mouse + touch
    // Cache rect to avoid repeated getBoundingClientRect calls during drag (reduces forced reflows)
    const rect = e.currentTarget.getBoundingClientRect();
    seekRectRef.current = rect;
    isSeekingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    seekByClientX(e.clientX, rect);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isSeekingRef.current) return;
    // Use cached rect from pointerDown to avoid repeated getBoundingClientRect calls
    // This reduces forced reflows during drag operations
    // Only recalculate if rect is not cached (shouldn't happen, but safety check)
    const rect = seekRectRef.current || e.currentTarget.getBoundingClientRect();
    seekByClientX(e.clientX, rect);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    isSeekingRef.current = false;
    seekRectRef.current = null; // Clear cached rect
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Use duration from state (which should be updated from audio element)
  const progress = duration > 0 
    ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) 
    : 0;
  const progressScaleX = progress / 100;
  const showHandle = hasPlayed;

  // Render tombstone if audioUrl is null, undefined, or empty string
  // Empty string causes "The element has no supported sources" error
  if (!audioUrl || audioUrl === null || audioUrl === undefined || audioUrl.trim() === '') {
    const tombstoneText = getTombstoneText(contentType, isOwner, deletedByType);
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <div className="flex-1 text-sm text-[#707090]">
          {tombstoneText}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <audio 
        ref={audioRef} 
        src={audioUrl} 
        preload="auto"
        onLoadedMetadata={(e) => {
          const audio = e.currentTarget;
          const audioDuration = audio.duration;
          if (audioDuration && !isNaN(audioDuration) && isFinite(audioDuration) && audioDuration > 0) {
            durationRef.current = audioDuration;
            setDuration(audioDuration);
          }
        }}
      />

      {/* Play/Pause Button - Mockup Exact: 36px circular gradient */}
      <button
        onClick={togglePlay}
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#ffe8f7] via-[#ff3ea5] to-[#b31973] text-[#120412] shadow-[0_0_14px_rgba(255,62,165,0.75)] transition hover:translate-y-[-1px] hover:shadow-[0_0_20px_rgba(255,62,165,0.9)]"
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? (
          <Pause className="h-4 w-4" fill="currentColor" />
        ) : (
          <Play className="h-4 w-4 translate-x-0.5" fill="currentColor" />
        )}
      </button>

      {/* Track (Progress Bar + Timestamps) */}
      <div className="flex flex-1 flex-col gap-1">
        {waveformUrl ? (
          <div
            className="relative h-16 cursor-pointer touch-none"
            onClick={handleSeek}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <img
              src={waveformUrl}
              alt="Waveform"
              className="h-full w-full object-cover"
            />
            <div
              className="absolute bottom-0 left-0 top-0 w-full origin-left bg-gradient-to-r from-[rgba(255,62,165,0.15)] to-[rgba(255,62,165,0.9)]"
              style={{ transform: `scaleX(${progressScaleX})` }}
            />
            {/* Scrubber handle (shows this is draggable) */}
            {showHandle ? (
              <div
                className="pointer-events-none absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-[#ffe8f7] shadow-[0_0_12px_rgba(255,232,247,0.55)]"
                style={{ left: `${progress}%`, transform: "translate(-50%, -50%)" }}
              />
            ) : null}
          </div>
        ) : (
          /* Clean Progress Bar with Glow */
          <div
            className="relative h-2 cursor-pointer touch-none"
            onClick={handleSeek}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            {/* Bar background + fill (clipped to rounded corners) */}
            <div className="absolute inset-0 overflow-hidden rounded-full bg-[#202032] shadow-inner">
              <div
                className="absolute left-0 top-0 h-full w-full origin-left bg-gradient-to-r from-[#ff3ea5] to-[#ff6ac2] shadow-[0_0_8px_rgba(255,62,165,0.6)]"
                style={{ transform: `scaleX(${progressScaleX})` }}
              />
            </div>
            {/* Scrubber handle (shows this is draggable) */}
            {showHandle ? (
              <div
                className="pointer-events-none absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ffe8f7] shadow-[0_0_12px_rgba(255,232,247,0.55)]"
                style={{ left: `${progress}%` }}
              />
            ) : null}
          </div>
        )}

        {/* Timestamps - Mockup Exact: 11px muted text */}
        <div className="flex justify-between text-[11px] text-[#a3a3c2]">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
});

PostPlayer.displayName = "PostPlayer";
